import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@bettercomp/ams-spa-auth-react';
import { amsPublicApiWorkspacesListSwitchableWorkspaces } from 'service/amsApi';
import type { SwitchableWorkspaceResponse } from 'connectors/ams';
import { reportError } from 'utils/reporting';

const switchableWorkspacesQueryKey = ['ams', 'workspaces', 'switchable'] as const;

type UseSwitchableWorkspacesReturn = {
  workspaces: SwitchableWorkspaceResponse[];
  isLoading: boolean;
  /**
   * True when the list could not be loaded. Callers must render something for this — an empty
   * `workspaces` is indistinguishable from a failed fetch otherwise, and a switcher that gates on
   * length would silently disappear on a 500 rather than telling the user it is broken.
   */
  isUnavailable: boolean;
};

/**
 * The workspaces the signed-in caller may switch into, from `GET /ams/workspaces/switchable`.
 *
 * Fetched eagerly rather than on dropdown-open, unlike the legacy `useTenantsLazy`: the legacy
 * switcher decides whether to render from the user's role, which is already in Redux, while the AMS
 * rule is "more than one switchable workspace" — so the answer is the list itself and it has to be
 * in hand before the control renders at all.
 *
 * Disabled without an AMS session. On the legacy path there is nothing to call: `/ams/workspaces/
 * switchable` requires an AMS bearer, and `service/amsApi` would send the request unauthenticated.
 */
export function useSwitchableWorkspaces(): UseSwitchableWorkspacesReturn {
  const { session, outcome } = useAuthContext();

  const { data, isPending, error } = useQuery({
    queryKey: switchableWorkspacesQueryKey,
    queryFn: async () => {
      // One report path for both failure shapes. An HTTP error arrives as a structured `error`
      // field, but a network fault, a timeout or a throw from fetchWithRenewal rejects instead —
      // and React Query surfaces either to the caller as a bare boolean, so anything not logged
      // here is invisible in error tracking.
      try {
        const { data, error } = await amsPublicApiWorkspacesListSwitchableWorkspaces();
        if (error) {
          // A 403 is not a fault. The route is @grant_required(WORKSPACE_SWITCH), so a caller
          // without that grant is simply not permitted to switch — which is most users: on
          // legacy-imported accounts workspace.switch comes only from manage_tenancy_switcher, and
          // on R1 roles anyone holding just survey_upload_manager lacks it. Treating it as
          // unavailable would put a permanent "Workspaces unavailable" in their header and a
          // reportError on every page load. Returned, not thrown, so it is never reported.
          if (error.code === 'forbidden') {
            return [];
          }
          // Attached as `cause` rather than interpolated: the generated client hands back the
          // thrown exception as `error` for a network-level failure, and an Error or DOMException
          // has no enumerable own properties, so JSON.stringify would render it "{}".
          throw new Error('Failed to list switchable workspaces', { cause: error });
        }
        return data ?? [];
      } catch (err) {
        reportError(err);
        throw err;
      }
    },
    // Gated on a minted token, not merely on the session existing. The session is constructed as
    // soon as /ams/auth/config resolves, which is before bootstrap() mints — so gating on
    // `Boolean(session)` fires this request while `auth: () => getToken()` still yields nothing.
    // AMS answers 401, `retry: false` means React Query never asks again, and the switcher reports
    // itself unavailable for the rest of the page's life even though the token arrives moments
    // later. Observed on dev-176717: one 401 with no Authorization header, no retry, and a valid
    // token sitting in the store immediately afterwards.
    enabled: Boolean(session) && outcome?.kind === 'minted',
    // A stale list mis-states which workspaces the caller may enter, and the switch itself is the
    // authority anyway — a revoked target answers 404 rather than trusting this cache.
    staleTime: 0,
    retry: false,
  });

  return {
    workspaces: data ?? [],
    isLoading: Boolean(session) && isPending,
    isUnavailable: Boolean(error),
  };
}
